import { Button, Col, Row, Spinner } from "react-bootstrap";
import { useEffect, useMemo, useRef, useState } from "react";

import { ComponentStruct, DocumentStruct } from "../../common/Interfaces";
import { useModels } from "../../api/simDocuments/ModelService";
import {
  PutModelApplicabilityAsync,
  PutModelApplicabilityParams,
} from "../../api/simDocuments/PutModelApplicabilityService";
import CheckBoxListModels from "../selectModels_components/CheckBoxListModels";
import SuccessPopUp from "../generic_components/SuccessPopUp";

export interface IChangeModelsParams {
  document: DocumentStruct;
  onErrorMessages: (errors: string[]) => void;
  show: boolean;
}

export default function ChangeModels({
  document,
  onErrorMessages,
  show,
}: IChangeModelsParams) {
  const documentReference = document.documentReference;
  const productCompanyCode = document.productCompany!.toString();
  const initialSelected = useMemo(
    () =>
      document.models!.map<ComponentStruct>((item) => ({
        key: item.code,
        label: item.description,
      })),
    [document.models]
  );

  const [selectedModels, setSelectedModels] = useState<ComponentStruct[]>([]);
  const [selectedArchivedModels, setSelectedArchivedModels] = useState<
    ComponentStruct[]
  >([]);
  const [showArchived, setShowArchived] = useState(false);
  const [modelUpdateButtonIsDisabled, setModelUpdateButtonIsDisabled] =
    useState(true);
  const initializedDocumentRef = useRef<string | null>(null);

  const { models, loading: loadingModels } = useModels({
    productCompany: productCompanyCode,
    productRange: "",
    archived: false,
  });

  const { models: archivedModels, loading: loadingArchivedModels } = useModels({
    productCompany: productCompanyCode,
    productRange: "",
    archived: true,
  });

  const [requestUpdate, setRequestUpdate] = useState(false);
  const [successPopUp, setSuccessPopUp] = useState(false);
  const updateModelsAsync = () => {
    setRequestUpdate(true);
  };

  useEffect(() => {
    if (
      initializedDocumentRef.current === documentReference ||
      loadingModels ||
      loadingArchivedModels
    ) {
      return;
    }

    const initialSelectedKeys = new Set(
      initialSelected.map((item) => item.key)
    );

    setSelectedModels(
      models.filter((item) => initialSelectedKeys.has(item.key))
    );
    setSelectedArchivedModels(
      archivedModels.filter((item) => initialSelectedKeys.has(item.key))
    );
    initializedDocumentRef.current = documentReference;
  }, [
    archivedModels,
    documentReference,
    initialSelected,
    loadingArchivedModels,
    loadingModels,
    models,
  ]);

  useEffect(() => {
    if (requestUpdate) {
      const requestModelApplicabilityChangeAsync = async (
        parameter: PutModelApplicabilityParams
      ) => {
        try {
          await PutModelApplicabilityAsync(parameter);
          onErrorMessages([]);
          setSuccessPopUp(true);
        } catch (error: any) {
          if (
            error.response &&
            error.response.data &&
            error.response.data.errors
          ) {
            const errors = error.response.data.errors;
            const messages = errors.map((err: any) => err.message);
            onErrorMessages(messages);
          } else {
            onErrorMessages([error.message]);
          }
        } finally {
          setRequestUpdate(false);
        }
      };

      const putModelApplicabilityParams = {
        documentReference: documentReference,
        productCompanyCode: productCompanyCode,
        models: selectedModels
          .concat(selectedArchivedModels)
          .map((item) => item.key),
      };
      requestModelApplicabilityChangeAsync(putModelApplicabilityParams);
    }

    const canUpdateModels: boolean =
      selectedModels.concat(selectedArchivedModels).length == 0;
    setModelUpdateButtonIsDisabled(canUpdateModels);
  }, [
    requestUpdate,
    documentReference,
    productCompanyCode,
    selectedModels,
    selectedArchivedModels,
  ]);

  return (
    <>
      {show && (
        <Row>
          <Col xs={8}>
            {loadingModels ? (
              <Spinner animation="border" />
            ) : (
              <CheckBoxListModels
                options={models}
                selected={selectedModels}
                archivedCheckbox={true}
                onChecked={(checked) => {
                  setShowArchived(checked);
                  if (!checked) {
                    setSelectedArchivedModels([]);
                  }
                }}
                onChange={(selectedItems) =>
                  setSelectedModels(selectedItems || [])
                }
              />
            )}
            {loadingArchivedModels ? (
              <Spinner animation="border" />
            ) : (
              <CheckBoxListModels
                options={archivedModels}
                selected={selectedArchivedModels}
                archivedCheckbox={false}
                show={showArchived}
                onChange={(selectedItems) =>
                  setSelectedArchivedModels(selectedItems || [])
                }
              />
            )}
          </Col>

          <Col>
            <Button
              className="btn btn-primary custom-button"
              disabled={modelUpdateButtonIsDisabled}
              onClick={updateModelsAsync}
            >
              Update models
            </Button>
          </Col>
        </Row>
      )}
      <SuccessPopUp
        message="Document was successfully updated"
        showPopup={successPopUp}
      />
    </>
  );
}
